import { useState } from "react";

import type { AdsOzetResponse } from "@/api/ads";
import { EmptyState } from "@/components/EmptyState";
import { FunnelSvg } from "@/components/FunnelSvg";
import { hasFunnelData, type FunnelStage } from "@/features/kpi/funnel";
import { t } from "@/i18n";

import { ADS_STORAGE_KEY } from "./AdsAralik";

export type ReklamKaynak = "tumu" | "meta" | "google";

interface ReklamHunisiProps {
  data: AdsOzetResponse | undefined;
  isLoading: boolean;
  isError: boolean;
}

function isReklamKaynak(v: unknown): v is ReklamKaynak {
  return v === "tumu" || v === "meta" || v === "google";
}

/**
 * Görev A: kaynak filtresi seçimi `AdsAralik`'in aralık seçimiyle AYNI
 * localStorage anahtarı altında (`asakai.kpi.adsRange`, bkz. AdsAralik.tsx
 * `ADS_STORAGE_KEY`) saklanır — burada yalnız `kaynak` alanı okunur/yazılır
 * (`range` alanına DOKUNULMAZ, read-modify-write).
 */
function readStoredKaynak(): ReklamKaynak {
  try {
    const raw = window.localStorage.getItem(ADS_STORAGE_KEY);
    if (!raw) return "tumu";
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    return isReklamKaynak(parsed.kaynak) ? parsed.kaynak : "tumu";
  } catch {
    return "tumu";
  }
}

function writeStoredKaynak(kaynak: ReklamKaynak): void {
  try {
    const raw = window.localStorage.getItem(ADS_STORAGE_KEY);
    const cur = raw ? (JSON.parse(raw) as Record<string, unknown>) : {};
    window.localStorage.setItem(ADS_STORAGE_KEY, JSON.stringify({ ...cur, kaynak }));
  } catch {
    // yut — tercih hatirlama sadece kolaylik, yoklugu islevi bozmaz.
  }
}

const SEKMELER: ReklamKaynak[] = ["tumu", "meta", "google"];

function etiketOf(k: ReklamKaynak): string {
  if (k === "tumu") return t("kpi.havuz.kaynak_tumu");
  // Not: `t("kpi.havuz.meta")`/`t("kpi.havuz.google")` OrtakHavuz'un kaynak
  // dagilimi etiketleri ("Meta"/"Google Ads") — "Google Ads" bu filtrede
  // YANLIS olurdu (gorev A metni: "Tümü"/"Meta"/"Google"), bu yuzden
  // "google" icin AYRI bir anahtar (`kaynak_google`) kullanilir.
  if (k === "meta") return t("kpi.havuz.meta");
  return t("kpi.havuz.kaynak_google");
}

/**
 * "Reklam dönüşüm hunisi" — görev A: Meta ve Google `AdsBlock`'larının
 * içindeki AYRI hunileri (eski `funnel` prop'u, KALDIRILDI — bkz.
 * AdsBlock.tsx) yerine `OrtakHavuz`'un ALTINDA TEK bir huni: üstte küçük bir
 * kaynak filtresi (Tümü/Meta/Google), altta 3 adımlı gösterim → tıklama →
 * dönüşüm. "Tümü" = `data.toplam` (Meta+Google birleşik — OrtakHavuz'daki 4
 * kartla AYNI kaynak); "Meta"/"Google" = `data.kaynaklar[]`'dan ilgili ağ.
 * Seçili kaynakta veri yoksa (`hasFunnelData` false) huni yerine "Veri yok"
 * (EmptyState) gösterilir.
 */
export function ReklamHunisi({ data, isLoading, isError }: ReklamHunisiProps) {
  const [kaynak, setKaynak] = useState<ReklamKaynak>(() => readStoredKaynak());

  function sec(k: ReklamKaynak) {
    setKaynak(k);
    writeStoredKaynak(k);
  }

  const kaynaklar = data?.kaynaklar ?? [];
  const meta = kaynaklar.find((k) => k.network === "meta");
  const google = kaynaklar.find((k) => k.network === "google");
  const seciliVeri = kaynak === "meta" ? meta : kaynak === "google" ? google : data?.toplam;

  const stages: FunnelStage[] | undefined = seciliVeri
    ? [
        { label: t("kpi.funnel.gosterim"), count: seciliVeri.impressions },
        { label: t("kpi.tiklama"), count: seciliVeri.clicks },
        ...(seciliVeri.conversions == null ? [] : [{ label: t("kpi.funnel.donusum"), count: seciliVeri.conversions }]),
      ]
    : undefined;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-display text-[15px] font-bold text-ink">{t("kpi.funnel.caption")}</h3>
        <div
          role="tablist"
          aria-label={t("kpi.havuz.kaynak_filtre")}
          className="inline-flex gap-0.5 rounded-[9px] bg-bg p-0.5"
        >
          {SEKMELER.map((k) => {
            const selected = kaynak === k;
            return (
              <button
                key={k}
                type="button"
                role="tab"
                aria-selected={selected}
                onClick={() => sec(k)}
                className={`min-h-[36px] rounded-[7px] px-3 text-[13px] font-semibold transition-colors ${
                  selected ? "bg-card text-brand-primary shadow-sm" : "text-muted hover:text-ink"
                }`}
              >
                {etiketOf(k)}
              </button>
            );
          })}
        </div>
      </div>

      <div className="card" data-testid="kpi-reklam-hunisi">
        {isLoading && <p className="text-sm text-muted">{t("common.loading")}</p>}
        {isError && <EmptyState kind="error" />}
        {!isLoading && !isError && (hasFunnelData(stages) ? <FunnelSvg stages={stages!} /> : <p className="text-sm text-muted">{t("kpi.havuz.huni_veri_yok")}</p>)}
      </div>
    </div>
  );
}
